import { useCallback, useEffect, useRef, useState } from "react";
import { DELEGATE_KEY, delegateApi, denyMessage, loadZoomEmbedded } from "./delegate";

const BASE = process.env.PUBLIC_URL || "";
const HANDOFF_KEY = "ciarb_room_handoff";
const ROOM_NAME = "ciarb-event-room";
export const SWITCH_MSG = "Switching windows will leave and rejoin the meeting. Continue?";

// Zoom component-view chrome below/above the video canvas (toolbar + title strip), kept inside the viewport.
export const ZOOM_CHROME = 130;
const MAX_TOP = 130; // stage is scrolled so the meeting root starts at most this far from the viewport top

// Video canvas size from the stage width and the space left in the viewport (16:9, width capped at Zoom's documented 1440 px).
export function videoSize(stageWidth, viewportHeight, rootTop) {
  const avail = viewportHeight - Math.min(Math.max(rootTop, 0), MAX_TOP) - ZOOM_CHROME - 12;
  const maxW = Math.max(240, Math.min(Math.floor(stageWidth), 1440));
  const height = Math.max(135, Math.min(Math.round((maxW * 9) / 16), avail));
  return { width: Math.max(240, Math.min(maxW, Math.round((height * 16) / 9))), height };
}

// Zoom gallery view / HD need cross-origin isolation, sent by the host only on a full page load: reload once after in-app navigation.
export function useIsolationReload() {
  useEffect(() => {
    if (window.isSecureContext && !window.crossOriginIsolated && performance.getEntriesByType("navigation")[0]?.type !== "reload") window.location.reload();
  }, []);
}

// Same-origin handoff of the delegate session to the event window: short-lived localStorage entry, consumed once, never in a URL.
export function consumeHandoff() {
  const raw = localStorage.getItem(HANDOFF_KEY);
  localStorage.removeItem(HANDOFF_KEY);
  try {
    const h = JSON.parse(raw);
    if (h?.t && h.exp > Date.now()) sessionStorage.setItem(DELEGATE_KEY, h.t);
  } catch { /* none */ }
}

// Opens (or focuses) the named event window. Returns false when the popup was blocked (caller falls back to same-tab navigation).
export function openEventWindow(beforeNavigate) {
  const t = sessionStorage.getItem(DELEGATE_KEY);
  if (t) localStorage.setItem(HANDOFF_KEY, JSON.stringify({ t, exp: Date.now() + 30000 }));
  setTimeout(() => localStorage.removeItem(HANDOFF_KEY), 30000);
  const w = window.open("", ROOM_NAME);
  if (!w) return false;
  let already = false;
  try { already = w.location.pathname === `${BASE}/event-room`; } catch { /* cross-origin blank */ }
  Promise.resolve(beforeNavigate?.()).then(() => { if (!already) w.location.href = `${BASE}/event-room`; w.focus(); });
  return true;
}

export const goToEventRoom = () => window.location.assign(`${BASE}/event-room`);

// One active meeting connection across tabs/windows (BroadcastChannel). Returns true when another tab holds the meeting.
function useMeetingLock(activeRef, onClaimed) {
  const chan = useRef(null);
  const id = useRef(Math.random().toString(36).slice(2));
  useEffect(() => {
    if (!("BroadcastChannel" in window)) return undefined;
    const c = new BroadcastChannel("ciarb-meeting");
    chan.current = c;
    c.onmessage = async ({ data }) => {
      if (data.from === id.current) return;
      if (data.type === "ping" && activeRef.current) c.postMessage({ type: "active", from: id.current });
      if (data.type === "claim" && activeRef.current) { await onClaimed(); c.postMessage({ type: "released", from: id.current }); }
    };
    return () => c.close();
  }, [activeRef, onClaimed]);
  const wait = (type, ms) => new Promise((ok) => {
    const c = chan.current;
    if (!c) return ok(false);
    const h = ({ data }) => { if (data.type === type && data.from !== id.current) { c.removeEventListener("message", h); ok(true); } };
    c.addEventListener("message", h);
    setTimeout(() => { c.removeEventListener("message", h); ok(false); }, ms);
  });
  const othersActive = async () => { const p = wait("active", 400); chan.current?.postMessage({ type: "ping", from: id.current }); return p; };
  const claim = async () => { const p = wait("released", 4000); chan.current?.postMessage({ type: "claim", from: id.current }); return p; };
  return { othersActive, claim };
}

// Readable text for raw Zoom SDK errors / connection-change payloads; the raw reason + code go to small "details" only.
const RULES = [
  [/not started|waiting for (the )?host|\b3008\b/i, "The host hasn't started the session yet. Please try again in a moment."],
  [/waiting.?room/i, "You're in the waiting room. The host will admit you shortly."],
  [/another meeting|in progress|already (in|joined)/i, "This account or device is already in the meeting elsewhere (for example the Zoom app). Close the other session, then try again."],
  [/signature|\b(3705|3706|3707|3712|124)\b/i, "Zoom did not accept the join authorisation. Please try again; if it keeps happening, contact the organisers."],
  [/passcode|password|\b3004\b/i, "The meeting passcode was not accepted. Please contact the organisers."],
  [/full|capacity|\b(3003|3010)\b/i, "The session is full. Please contact the organisers."],
  [/removed|expel|kicked/i, "The host removed you from the session."],
  [/ended|end meeting/i, "The session has ended."],
  [/network|websocket|timeout|offline|connect/i, "A network problem interrupted the connection to Zoom. Check your connection and try again."],
];
export function describeZoom(e) {
  const reason = e?.reason || e?.type || e?.message || "";
  const hit = RULES.find(([re]) => re.test(`${reason} ${e?.errorCode ?? ""}`));
  const details = reason || e?.errorCode != null ? `Zoom: ${reason}${e?.errorCode != null ? ` (code ${e.errorCode})` : ""}` : "";
  return { message: hit ? hit[1] : "Couldn't connect to the session.", details };
}
const isReconnect = (e) => e?.errorCode === 3 || /reconnect/i.test(`${e?.reason || ""} ${e?.type || ""}`);
const ACTIVE = ["loading", "connecting", "waiting", "joined", "reconnecting"];
const WAITING_AFTER_MS = 10000;

// Stage timings (ms since the Join click), console only; never tokens, signatures or passcodes.
function useTimings() {
  const t = useRef({});
  return useRef({
    start: () => { t.current = { t0: performance.now() }; },
    mark: (k) => {
      if (t.current.t0 == null || t.current[k] != null) return;
      t.current[k] = Math.round(performance.now() - t.current.t0);
      console.info(`[event-room] ${k} +${t.current[k]} ms`);
    },
    all: () => { const { t0, ...rest } = t.current; return rest; },
  }).current;
}

// State machine: idle -> loading (signature, SDK script, init) -> connecting (join call) -> waiting (host admission)
// -> joined (Connected) <-> reconnecting -> left / error. One client at a time (single-flight), destroyed on leave/failure.
export function useZoomMeeting(theatre) {
  const root = useRef(null);
  const stage = useRef(null);
  const client = useRef(null);
  const busy = useRef(false);
  const leaving = useRef(false);
  const activeRef = useRef(false);
  const timing = useTimings();
  const [state, setState] = useState({ phase: "idle", message: "", details: "" });
  const [dims, setDims] = useState(null);
  const active = ACTIVE.includes(state.phase);
  activeRef.current = active;
  const size = useCallback(() => {
    const st = stage.current;
    if (!st) return videoSize(960, window.innerHeight, MAX_TOP);
    const top = root.current ? root.current.getBoundingClientRect().top : st.getBoundingClientRect().top;
    return videoSize((root.current?.parentElement || st).clientWidth, window.innerHeight, top);
  }, []);
  const fit = useCallback(() => {
    const st = stage.current;
    if (st && root.current && root.current.getBoundingClientRect().top > MAX_TOP) st.scrollIntoView({ block: "start" });
    const d = size();
    setDims(d);
    return d;
  }, [size]);

  const teardown = useCallback(() => {
    try { (window.__CIARB_ZOOM_MOCK__ || window.ZoomMtgEmbedded)?.destroyClient?.(); } catch { /* already destroyed */ }
    client.current = null;
    busy.current = false;
  }, []);
  const leave = useCallback(async (message = "") => {
    leaving.current = true;
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
    try { await client.current?.leaveMeeting(); } catch { /* already closed */ }
    teardown();
    setState({ phase: "left", message, details: "" });
    leaving.current = false;
  }, [teardown]);
  const onClaimed = useCallback(() => leave("The meeting was moved to another window."), [leave]);
  const lock = useMeetingLock(activeRef, onClaimed);
  useEffect(() => () => teardown(), [teardown]);

  // Re-apply the size through the supported SDK call after any layout change while in the meeting.
  const inMeeting = state.phase === "joined" || state.phase === "reconnecting";
  useEffect(() => {
    if (!inMeeting || !stage.current) return undefined;
    let t;
    const apply = () => { clearTimeout(t); t = setTimeout(() => { const d = fit(); try { client.current?.updateVideoOptions({ viewSizes: { default: d } }); } catch { /* not ready */ } }, 200); };
    const ro = new ResizeObserver(apply);
    ro.observe(stage.current);
    if (root.current?.parentElement) ro.observe(root.current.parentElement);
    window.addEventListener("resize", apply);
    document.addEventListener("fullscreenchange", apply);
    apply();
    return () => { ro.disconnect(); window.removeEventListener("resize", apply); document.removeEventListener("fullscreenchange", apply); clearTimeout(t); };
  }, [inMeeting, theatre, fit]);

  const onConnection = useCallback((p) => {
    const st = p?.state;
    if (st === "Connected") {
      timing.mark("connected");
      console.info("[event-room] timings (ms since Join click)", timing.all());
      setState({ phase: "joined", message: "", details: "" });
    } else if (st === "Reconnecting") setState({ phase: "reconnecting", message: "Reconnecting…", details: "" });
    else if (st === "Connecting" && /waiting.?room/i.test(p?.reason || "")) {
      timing.mark("waiting-room");
      setState({ phase: "waiting", message: "Waiting for the host to admit you…", details: "" });
    } else if (st === "Closed" && !leaving.current) {
      teardown();
      setState({ phase: "left", ...(p?.reason ? describeZoom(p) : { message: "", details: "" }) });
    } else if (st === "Fail") { teardown(); setState({ phase: "error", ...describeZoom(p) }); }
  }, [teardown, timing]);

  const join = async () => {
    if (busy.current) return; // single-flight: repeated clicks never create a second client or a second join
    busy.current = true;
    try {
      if (await lock.othersActive()) {
        if (!window.confirm(SWITCH_MSG)) { busy.current = false; return; }
        await lock.claim();
      }
      timing.start();
      setState({ phase: "loading", message: "", details: "" });
      const { data } = await delegateApi.post("/virtual/signature");
      timing.mark("signature");
      const ZoomMtgEmbedded = await loadZoomEmbedded(data.sdkVersion);
      timing.mark("sdk-script");
      try { ZoomMtgEmbedded.destroyClient(); } catch { /* none */ }
      const c = ZoomMtgEmbedded.createClient();
      client.current = c;
      await c.init({ zoomAppRoot: root.current, language: "en-US", patchJsMedia: true, leaveOnPageUnload: true,
        customize: { video: { isResizable: true, viewSizes: { default: fit() }, popper: { disableDraggable: true }, defaultViewType: "speaker" } } });
      timing.mark("init");
      c.on("connection-change", onConnection);
      setState({ phase: "connecting", message: "", details: "" });
      const slow = setTimeout(() => setState((s) => (s.phase === "connecting"
        ? { phase: "waiting", message: "Still connecting… If the host uses a waiting room, you'll be admitted shortly.", details: "" } : s)), WAITING_AFTER_MS);
      try {
        await c.join({ signature: data.signature, meetingNumber: data.meetingNumber, password: data.password,
          userName: data.userName, userEmail: data.userEmail, ...(data.tk ? { tk: data.tk } : {}) });
        timing.mark("join-resolved");
        setState((s) => (["reconnecting", "left", "error", "joined"].includes(s.phase) ? s : { phase: "joined", message: "", details: "" }));
      } finally { clearTimeout(slow); }
    } catch (e) {
      if (isReconnect(e) && client.current) { setState({ phase: "reconnecting", message: "Reconnecting…", details: "" }); return; }
      teardown();
      setState(e?.response ? { phase: "error", message: denyMessage(e), details: "" } : { phase: "error", ...describeZoom(e) });
    }
  };
  // Root is a centred block exactly as wide as the video (Zoom positions its content from the root's left edge).
  const rootStyle = active && dims ? { width: dims.width, minHeight: dims.height + ZOOM_CHROME, margin: "0 auto", position: "relative" }
    : { width: "100%", ...(state.phase === "error" || state.phase === "left" ? { display: "none" } : {}) };
  return { root, stage, state, active, join, leave, rootStyle, dims, joining: active && !inMeeting };
}
